/**
 * Force `muted` onto a <video> as an ATTRIBUTE, not just a property.
 *
 * React declares `muted` with `mustUseProperty`, so `<video muted>` in JSX only
 * ever sets `el.muted` — the attribute is never written to the DOM. Two things
 * in this app break on that, and together they froze the homepage hero for
 * every cold visitor (#582):
 *
 *  1. Marketing routes are PRERENDERED. scripts/prerender.js drives headless
 *     Chromium through the route and saves `document.documentElement.outerHTML`.
 *     A property is not serialisable, so the HTML actually shipped read
 *     `<video autoplay playsinline>` with no `muted` — and Chrome's autoplay
 *     policy refuses an unmuted autoplay on a site with no media-engagement
 *     history. Playback was dead before React had even loaded.
 *  2. React 18 hydration does not apply properties to nodes it matches against
 *     existing markup, so `el.muted` stayed FALSE after hydrate too. The
 *     effect's `play()` was rejected as well, `autoplayBlocked` flipped, and the
 *     cells sat on their poster behind a native control bar.
 *
 * Writing the attribute fixes the served markup — the parser starts playback
 * before any JS runs — and writing the property covers the hydrated node.
 * Both, because neither alone covers both paths.
 *
 * WHY THIS LIVES IN lib/ RATHER THAN NEXT TO ITS FIRST CALLER
 * The hole is not specific to the MotionPace hero: every autoplaying clip on a
 * prerendered marketing route has it (the flag-off 2-up hero in LandingPage,
 * the /motionpace demo). Its first home was inside `landing/motionPaceHome.js`,
 * which LandingPage `require`s only behind the MotionPace flag precisely so
 * webpack drops that module — and its unlaunched video assets — from production
 * builds. Importing the helper from there would have dragged the gated module
 * into every build. Here it is a two-line pure function with no assets, so it
 * is safe for gated and ungated callers alike. Same reasoning as
 * lib/prefersReducedMotion.js beside it.
 *
 * Idempotent: safe to call on every ref attach, including a re-attach of a node
 * that already has the attribute.
 *
 * @param {HTMLVideoElement | HTMLAudioElement} el
 */
const forceMuted = (el) => {
  if (!el) return;
  // eslint-disable-next-line no-param-reassign
  el.muted = true;
  if (!el.hasAttribute('muted')) el.setAttribute('muted', '');
};

export default forceMuted;
